<script setup lang="ts">
import { formatToFullVisibleAddress } from '@/utils/formatToFullVisibleAddress'

import type { AddressClient } from '@/api/modules/addresses/addresses.types'

const BORDER_VISIBLE_FLAT = 3

type Props = {
  address: AddressClient
  isActive: boolean
}

const props = defineProps<Props>()

// Проверка на то, что адрес вне зоны доставки
const isNotDeliveryAreaAddress = computed(() => {
  return !props.address.is_in_delivery_area
})

const componentClass = computed(() => {
  const classes = ['user-list-item']

  if (props.isActive) classes.push('user-list-item--active')

  if (isNotDeliveryAreaAddress.value) classes.push('user-list-item--disabled')

  return classes
})

const correctAddressCityStreet = computed(() => {
  const { city, alias } = props.address

  return `г. ${city || alias}, ${formatToFullVisibleAddress(props.address, {
    house: false,
  })}`
})

const correctAddressHouseFlat = computed(() => {
  const { house, flat } = props.address

  // TODO: В будущем добавить валидацию для корректных полей квартиры, подъезда и.т.д
  return flat && flat.length <= BORDER_VISIBLE_FLAT
    ? `д. ${house}, кв. ${flat}`
    : `д. ${house}`
})
</script>

<template>
  <div
    role="option"
    :class="componentClass"
    :tabindex="-Number(isActive)"
    :aria-selected="isActive"
    :aria-disabled="isNotDeliveryAreaAddress"
  >
    <div class="user-list-item__content">
      <the-icon
        size="24"
        name="location--filled"
        color="var(--ui-primary)"
        class="user-list-item__icon"
      />
      <div class="user-list-item__address">
        <span class="user-list-item__address-street">
          {{ correctAddressCityStreet }}
        </span>
        <span class="user-list-item__address-house">
          ,&nbsp;{{ correctAddressHouseFlat }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.user-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: calc(100% - 4px);
  padding: 16px 16px;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  color: var(--ui-secondary);
  user-select: none;
  background-color: var(--ui-grey);
  border-radius: 6px;

  @include media('>=tablet') {
    width: 100%;
  }

  &:focus-visible {
    background-color: var(--ui-grey--hover);
    outline: none;
  }

  &--active {
    padding: 14px 14px;
    border: 2px solid var(--ui-green);

    &:focus-visible {
      background-color: var(--ui-grey);
      border: 2px solid var(--ui-green);
      outline: none;
    }
  }

  &--disabled {
    pointer-events: none;
    cursor: not-allowed;
    outline: none;
    opacity: 0.5;
  }

  &:hover:not(&--disabled) {
    cursor: pointer;
    background-color: var(--ui-grey--hover);
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-right: 16px;
  }

  &__icon {
    margin-right: 8px;
  }

  &__address {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 16px;

    &-street {
      display: -webkit-box;
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-all;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
      line-clamp: 1;
    }

    &-house {
      white-space: nowrap;
    }
  }
}
</style>
